/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { Suspense, useCallback } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import styled, { ThemeProvider } from "styled-components";
import GlobalWidgetStyle, { widgetResetStyles } from "./components/GlobalWidgetStyle";
import routes from "./routes";
import Layout from "./Layout";
import MinimalLayout from "./MinimalLayout";
import LoadingIndicator from "./components/LoadingIndicator";
import ErrorBoundary, { WidgetErrorFallback, RouteErrorFallback } from "./components/ErrorBoundary";
import useWidgetTheme from "./hooks/useWidgetTheme";
import { AppInitializer } from "./context/AppInitializer";
import { AuthProvider } from "./hooks/useAuth";
import { Provider } from "react-redux";
import { store } from "./store";
import { BootGate } from "./context/BootGate";
import DefaultRouteHandler from "./components/DefaultRouteHandler";
import OnlineMeeting from "./pages/OnlineMeeting";
import { ROUTES } from "./constants/constants";
import { ToastManager } from "./components/ui/Toast";

const StyledApp = styled.section`
  container-name: sb-widget;
  container-type: inline-size;
  max-width: 700px;
  margin: 0 auto;
  min-height: 250px;
  line-height: 1.5;
  background: rgba(${({ theme }) => theme.colors.background.rgba});

  ${widgetResetStyles}
`;

const RouteErrorBoundary = ({ children }) => {
  const navigate = useNavigate();

  const handleReset = useCallback(() => {
    navigate("/");
  }, [navigate]);

  return (
    <ErrorBoundary fallback={RouteErrorFallback} onReset={handleReset}>
      {children}
    </ErrorBoundary>
  );
};

function App({ companyName, companyKey, theme }) {
  const themeSettings = useWidgetTheme(theme);

  return (
    <ThemeProvider theme={themeSettings}>
      <StyledApp aria-label={companyName || undefined}>
        <GlobalWidgetStyle />
        <ToastManager />
        <ErrorBoundary fallback={WidgetErrorFallback}>
            <Suspense fallback={<LoadingIndicator page />}>
              <Provider store={store}>
                <AppInitializer companyName={companyName} companyKey={companyKey}>
                  <AuthProvider>
                    <BootGate>
                      <RouteErrorBoundary>
                        <Routes>
                            <Route element={<MinimalLayout />}>
                              <Route path={ROUTES.ONLINE_MEETING} element={<OnlineMeeting />} />
                            </Route>
                            <Route path="/" element={<Layout />}>
                              <Route index element={<DefaultRouteHandler />} />
                              {routes.map((route, index) => (
                                <Route key={index} path={route.path} element={route.element} exact={route.exact} />
                              ))}
                            </Route>
                        </Routes>
                      </RouteErrorBoundary>
                    </BootGate>
                  </AuthProvider>
                </AppInitializer>
              </Provider>
            </Suspense>
          </ErrorBoundary>
      </StyledApp>
    </ThemeProvider>
  );
}

export default App;
